import React, { useState, useEffect } from "react";
import { X, User, CreditCard, Calendar, Settings } from "lucide-react";
import { toast } from "react-toastify";

const SUBSCRIPTION_STATUS_NO_SUBSCRIPTION = 'No Subscription'
const SUBSCRIPTION_STATUS_ACTIVE = 'Active'
const SUBSCRIPTION_STATUS_PAYMENT_PENDING = 'Payment Pending'
const SUBSCRIPTION_STATUS_CANCELED = "Cancelled"
const SUBSCRIPTION_STATUS_GRACE_PERIOD = "Grace Period"
const SUBSCRIPTION_STATUS_UNLISTED = "Unlisted Due to Payment"

const SUBSCRIPTION_PLAN_NO_SUBSCRIPTION = 'No Subscription'
const SUBSCRIPTION_PLAN_30_MINUTE = "30 Minute"
const SUBSCRIPTION_PLAN_HOURLY = "Hourly"
const SUBSCRIPTION_PLAN_DAILY = "Daily"
const SUBSCRIPTION_PLAN_MONTHLY = "Monthly"
const SUBSCRIPTION_PLAN_6_MONTH = "6 Month"
const SUBSCRIPTION_PLAN_ANNUAL = "Annual"
const SUBSCRIPTION_PLAN_GRANDFATHERED = "Grandfathered"

const SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID = 'none'
const SUBSCRIPTION_PLAN_30_MINUTE_ID = "30minute"
const SUBSCRIPTION_PLAN_HOURLY_ID = "hourly"
const SUBSCRIPTION_PLAN_DAILY_ID = "daily"
const SUBSCRIPTION_PLAN_MONTHLY_ID = "monthly"
const SUBSCRIPTION_PLAN_6_MONTH_ID = "6month"
const SUBSCRIPTION_PLAN_ANNUAL_ID = "annual"
const SUBSCRIPTION_PLAN_GRANDFATHERED_ID = "grandfathered"

const APP_ENV = process.env.REACT_APP_APP_ENV


const plansData = APP_ENV == 'dev' || APP_ENV == 'staging' ? [
  {plan: SUBSCRIPTION_PLAN_NO_SUBSCRIPTION, planId: SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID },

  {plan: SUBSCRIPTION_PLAN_30_MINUTE, planId: SUBSCRIPTION_PLAN_30_MINUTE_ID },
  {plan: SUBSCRIPTION_PLAN_HOURLY, planId: SUBSCRIPTION_PLAN_HOURLY_ID },
  {plan: SUBSCRIPTION_PLAN_DAILY, planId: SUBSCRIPTION_PLAN_DAILY_ID },

  {plan: SUBSCRIPTION_PLAN_MONTHLY, planId: SUBSCRIPTION_PLAN_MONTHLY_ID },
  {plan: SUBSCRIPTION_PLAN_6_MONTH, planId: SUBSCRIPTION_PLAN_6_MONTH_ID },
  {plan: SUBSCRIPTION_PLAN_ANNUAL, planId: SUBSCRIPTION_PLAN_ANNUAL_ID },
  {plan: SUBSCRIPTION_PLAN_GRANDFATHERED, planId: SUBSCRIPTION_PLAN_GRANDFATHERED_ID },
] : [{plan: SUBSCRIPTION_PLAN_NO_SUBSCRIPTION, planId: SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID },
  {plan: SUBSCRIPTION_PLAN_MONTHLY, planId: SUBSCRIPTION_PLAN_MONTHLY_ID },
  {plan: SUBSCRIPTION_PLAN_6_MONTH, planId: SUBSCRIPTION_PLAN_6_MONTH_ID },
  {plan: SUBSCRIPTION_PLAN_ANNUAL, planId: SUBSCRIPTION_PLAN_ANNUAL_ID },
  {plan: SUBSCRIPTION_PLAN_GRANDFATHERED, planId: SUBSCRIPTION_PLAN_GRANDFATHERED_ID },]


function isoToDatetimeLocal(isoString) {
  const date = new Date(isoString);
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Helper function to determine field requirements based on status
const getDateFieldRules = (status) => {
  switch(status) {
    case SUBSCRIPTION_STATUS_ACTIVE:
    case SUBSCRIPTION_STATUS_GRACE_PERIOD:
      return {
        endOfGracePeriod: { required: status == SUBSCRIPTION_STATUS_GRACE_PERIOD, disabled: status !== SUBSCRIPTION_STATUS_GRACE_PERIOD },
        subscriptionStartDate: { required: true, disabled: false },
        nextRenewDate: { required: true, disabled: false }
      };
    case SUBSCRIPTION_STATUS_NO_SUBSCRIPTION:
    case SUBSCRIPTION_STATUS_UNLISTED:
    case SUBSCRIPTION_STATUS_CANCELED:
      return {
        endOfGracePeriod: { required: false, disabled: true, shouldClear: true },
        subscriptionStartDate: { required: false, disabled: true, shouldClear: true },
        nextRenewDate: { required: false, disabled: true, shouldClear: true }
      };
    case SUBSCRIPTION_STATUS_PAYMENT_PENDING:
      return {
        endOfGracePeriod: { required: false, disabled: true, shouldClear: true },
        subscriptionStartDate: { required: false, disabled: false },
        nextRenewDate: { required: false, disabled: false }
      };
    default:
      return {
        endOfGracePeriod: { required: false, disabled: true },
        subscriptionStartDate: { required: false, disabled: false },
        nextRenewDate: { required: false, disabled: false }
      };
  }
};

export default function SubscriptionForm({
  initialData = {},
  onSubmit,
  onClose,
  requesting,
  onPropertiesUpdate,
  onCancelAtRenewal,
  onResumeRenewal,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState({
    userId: initialData.userId || "",
    status: initialData.status || "",
    plan: initialData.plan || "",
    planId: initialData.planId || "",
    endOfGracePeriod: initialData.endOfGracePeriod && initialData.status === SUBSCRIPTION_STATUS_GRACE_PERIOD 
      ? isoToDatetimeLocal(initialData.endOfGracePeriod) 
      : "",
    subscriptionStartDate: initialData.subscriptionStartDate ? isoToDatetimeLocal(initialData.subscriptionStartDate)  : "",
    nextRenewDate: initialData.nextRenewDate ? isoToDatetimeLocal(initialData.nextRenewDate)  : "",
    upcomingPlan: initialData.upcomingPlan || "",
    cancelOnNextRenew: initialData.cancelOnNextRenew || false,
    processing: initialData.processing || false,
    // primaryMethod: initialData.primaryMethod || "",
    // updatePaymentMethodRequired: initialData.updatePaymentMethodRequired || false,
    // isPrimaryCardExpired: initialData.isPrimaryCardExpired || false,
    // processing: initialData.processing || false,
  });

  const [plans, setPlans] = useState([])

  const [upcomingPlans, setUpcomingPlans] = useState([])


  // Get current date field rules based on status
  const dateFieldRules = getDateFieldRules(formData.status);

  const getAvailablePlans = (status) => {
    if (status === SUBSCRIPTION_STATUS_NO_SUBSCRIPTION || status === SUBSCRIPTION_STATUS_UNLISTED) {
      return plansData.filter(plan => plan.planId === SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID || plan.planId === SUBSCRIPTION_PLAN_GRANDFATHERED_ID);
    } else {
      return plansData.filter(plan => plan.planId !== SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID && plan.planId !== SUBSCRIPTION_PLAN_GRANDFATHERED_ID);
    }
  };

  const getAvailableUpcomingPlans = (status) => {
    if (status === SUBSCRIPTION_STATUS_NO_SUBSCRIPTION || status === SUBSCRIPTION_STATUS_UNLISTED || status == SUBSCRIPTION_STATUS_CANCELED) {
      return []
    } else {
      return plansData.filter(plan => plan.planId !== SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID && plan.planId !== SUBSCRIPTION_PLAN_GRANDFATHERED_ID);
    }
  };


  // Effect to handle status changes and manage date fields based on rules
  useEffect(() => {

    const rules = getDateFieldRules(formData.status);
    const availablePlans = getAvailablePlans(formData.status);
    const availableUpcomingPlans = getAvailableUpcomingPlans(formData.status)
    
    setFormData(prev => {
      const newData = { ...prev };
      
      // Clear fields that should be cleared for the current status
      Object.keys(rules).forEach(fieldName => {
        if (rules[fieldName].shouldClear) {
          newData[fieldName] = "";
        }
      });

      
      if (formData.status === SUBSCRIPTION_STATUS_UNLISTED || (formData.status === SUBSCRIPTION_STATUS_NO_SUBSCRIPTION && formData.planId != SUBSCRIPTION_PLAN_GRANDFATHERED_ID)) {
        newData.plan = SUBSCRIPTION_PLAN_NO_SUBSCRIPTION;
        newData.planId = SUBSCRIPTION_PLAN_NO_SUBSCRIPTION_ID;
      }
      if(![SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_GRACE_PERIOD, SUBSCRIPTION_STATUS_PAYMENT_PENDING].includes(formData.status)) {
        newData.upcomingPlan = ''
      }
      setPlans(availablePlans)
      setUpcomingPlans(availableUpcomingPlans)
      
      return newData;
    });
  }, [formData.status]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('handleChange', name, value, type, checked )
    
    if(name === 'plan') {
      const plan = plans.find((planData) => {
        return planData.plan === value
      })
      setFormData((prev) => ({
        ...prev,
        plan: plan ? plan.plan : '',
        planId: plan ? plan.planId : ''
      }));
    } else if (name === 'status') {
      setFormData((prev) => ({
        ...prev,
        status: value,
      }));
    } else if (name == 'updatePaymentMethodRequired') {
      setFormData((prev) => ({
        ...prev,
        isPrimaryCardExpired: checked,
        updatePaymentMethodRequired: checked
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required date fields based on current status
    const rules = getDateFieldRules(formData.status);
    const errors = [];
    
    Object.keys(rules).forEach(fieldName => {
      if (rules[fieldName].required && !formData[fieldName]) {
        const fieldLabel = fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        errors.push(`${fieldLabel} is required for ${formData.status} status`);
      }
    });

    if (!formData.status) errors.push("Status is required")
    if (!formData.plan) { 
      errors.push("Plan is required")
    } else {
      const plans = getAvailablePlans(formData.status)
      if(!plans.find((value) => {
        return value.planId == formData.planId
      })) {
        errors.push("Plan is required")
      }
    }
    
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }
    
    const newUpcoming = formData.cancelOnNextRenew || ![SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_GRACE_PERIOD, SUBSCRIPTION_STATUS_PAYMENT_PENDING].includes(formData.status) ? null : formData.upcomingPlan == '' ? null : formData.upcomingPlan
    const toTimestampOrNull = value => value ? new Date(value).getTime() : null
    // Keep cancellation and worker-owned processing state out of the legacy
    // broad update. Dedicated commands own those fields.
    const finalFormData = {
      userId: formData.userId,
      status: formData.status,
      plan: formData.plan,
      planId: formData.planId,
      upcomingPlan:  newUpcoming,
      endOfGracePeriod: toTimestampOrNull(formData.endOfGracePeriod),
      subscriptionStartDate: toTimestampOrNull(formData.subscriptionStartDate),
      nextRenewDate: toTimestampOrNull(formData.nextRenewDate),
    };
    
    if(onSubmit) onSubmit(finalFormData);
  };

  const canScheduleCancellation = [
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_GRACE_PERIOD,
    SUBSCRIPTION_STATUS_PAYMENT_PENDING,
  ].includes(formData.status);
  const effectiveRenewalDate = formData.cancelOnNextRenew
    ? initialData.cancellationEffectiveAt || formData.nextRenewDate
    : formData.nextRenewDate;
  const renewalDateLabel = effectiveRenewalDate
    ? new Date(effectiveRenewalDate).toLocaleString()
    : "No renewal date is set";

  const renderDateField = (fieldName, label) => {
    const rules = dateFieldRules[fieldName];
    const isRequired = rules ? rules.required : false;
    const isDisabled = rules ? rules.disabled : false;
    
    return (
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
          {label} {isRequired && <span className="tw-text-red-500">*</span>}
        </label>
        <input
          type="datetime-local"
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleChange}
          disabled={isDisabled}
          required={isRequired}
          className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200 ${
            isDisabled ? 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed' : ''
          } ${isRequired ? 'tw-border-blue-300' : ''}`}
        />
        {isDisabled && (
          <span className="tw-text-xs tw-text-gray-500 tw-ml-1">
            (Not available for {formData.status == null || formData.status == '' ? 'this' : formData.status} status)
          </span>
        )}
        {isRequired && !isDisabled && (
          <span className="tw-text-xs tw-text-blue-600 tw-ml-1">
            (Required for {formData.status} status)
          </span>
        )}
      </div>
    );
  };

  return (
    <div className=" tw-flex tw-items-center tw-justify-center tw-p-4 tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto">
        <div className="tw-p-8">
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-8">
            <div className="tw-flex tw-flex-row tw-items-center tw-h-full tw-gap-2">
              <div className="tw-w-10 tw-h-10 tw-bg-gradient-to-br tw-from-blue-500 tw-to-purple-600 tw-rounded-xl tw-flex tw-items-center tw-justify-center">
                <Settings className="tw-w-5 tw-h-5 tw-text-white" />
              </div>
              <h2 className="tw-flex tw-h-10 tw-my-auto tw-items-center tw-text-2xl tw-font-bold tw-text-gray-900">Edit Subscription</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-p-2 hover:tw-bg-gray-100 tw-rounded-xl tw-transition-colors tw-duration-200"
            >
              <X className="tw-w-5 tw-h-5 tw-text-gray-500" />
            </button>
          </div>

          <div className="tw-space-y-8">
            {/* Basic Information Section */}
            <div className="tw-space-y-6">
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-border-b tw-border-gray-200 tw-pb-2">
                <User className="tw-w-5 tw-h-5" />
                Basic Information
              </div>
              
              <div className="tw-space-y-4">
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">User ID</label>
                  <input
                    readOnly={true}
                    type="text"
                    name="userId"
                    value={formData.userId}
                    onChange={handleChange}
                    className="tw-w-full tw-px-4 tw-py-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200"
                    required
                  />
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Plan</label>
                    <select
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      disabled={requesting || formData.cancelOnNextRenew}
                      className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200 ${formData.cancelOnNextRenew ? 'tw-bg-gray-100 tw-cursor-not-allowed' : ''}`}
                      required
                    >
                      <option value={null}>Select plan</option>
                      {plans.map((plan) => {
                        return <option key={plan.planId} value={plan.plan}>{plan.plan}</option>
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={requesting || formData.cancelOnNextRenew}
                      className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200 ${formData.cancelOnNextRenew ? 'tw-bg-gray-100 tw-cursor-not-allowed' : ''}`}
                      required
                    >
                      <option value="">Select status</option>
                      <option value={SUBSCRIPTION_STATUS_NO_SUBSCRIPTION}>No subscription</option>
                      <option value={SUBSCRIPTION_STATUS_ACTIVE}>Active</option>
                      <option value={SUBSCRIPTION_STATUS_PAYMENT_PENDING}>Payment Pending</option>
                      <option value={SUBSCRIPTION_STATUS_CANCELED}>Cancelled</option>
                      <option value={SUBSCRIPTION_STATUS_GRACE_PERIOD}>Grace Period</option>
                      <option value={SUBSCRIPTION_STATUS_UNLISTED}>Unlisted</option>
                    </select>
                  </div>
                </div>

                {/* Status-based Date Rules Info */}
                {formData.status && (
                  <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                    <h4 className="tw-font-medium tw-text-blue-900 tw-mb-2">Date Field Rules for {formData.status}:</h4>
                    <ul className="tw-text-sm tw-text-blue-800 tw-space-y-1">
                      {formData.status === SUBSCRIPTION_STATUS_ACTIVE && (
                        <li>• All date fields are required (Start Date, Renew Date)</li>
                      )}
                      {formData.status === SUBSCRIPTION_STATUS_GRACE_PERIOD && (
                        <li>• All date fields are required (End of Grace Period, Start Date, Renew Date)</li>
                      )}
                      {(formData.status === SUBSCRIPTION_STATUS_NO_SUBSCRIPTION || formData.status === SUBSCRIPTION_STATUS_UNLISTED || formData.status === SUBSCRIPTION_STATUS_CANCELED) && (
                        <li>• All date fields will be cleared and disabled</li>
                      )}
                      {formData.status === SUBSCRIPTION_STATUS_PAYMENT_PENDING && (
                        <li>• All date fields are optional</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="tw-space-y-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
              <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                <div>
                  <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">Renewal control</h3>
                  <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-gray-700">
                    {formData.cancelOnNextRenew
                      ? `Cancellation is scheduled for the next renewal (${renewalDateLabel}).`
                      : `Subscription renewal is active (${renewalDateLabel}).`}
                  </p>
                  <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-gray-500">
                    Payment processing: {formData.processing ? 'In progress' : 'Idle'}
                  </p>
                  {formData.cancelOnNextRenew && initialData.cancellationRequestedAt && (
                    <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-gray-500">
                      Requested {new Date(initialData.cancellationRequestedAt).toLocaleString()}
                    </p>
                  )}
                  {formData.cancelOnNextRenew && (
                    <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-amber-700">
                      Resume renewal before changing the current or upcoming plan.
                    </p>
                  )}
                </div>
                {formData.cancelOnNextRenew ? (
                  <button
                    type="button"
                    onClick={onResumeRenewal}
                    disabled={requesting || !onResumeRenewal}
                    className="btn btn-primary"
                  >
                    Resume renewal
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onCancelAtRenewal}
                    disabled={requesting || !canScheduleCancellation || !onCancelAtRenewal}
                    className="btn btn-danger"
                    title={canScheduleCancellation ? '' : 'This subscription status cannot be cancelled at renewal'}
                  >
                    Cancel at renewal
                  </button>
                )}
              </div>
            </div>

            {/* Advanced Toggle */}
            <div className="tw-border-t tw-border-gray-200 tw-pt-2 tw-mt-2">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 hover:tw-text-blue-800 tw-font-medium tw-transition-colors tw-duration-200 tw-bg-blue-50 tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-blue-100"
              >
                <Settings className="tw-w-4 tw-h-4" />
                {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
              </button>
            </div>

            {/* Advanced Fields */}
            {showAdvanced && (
              <>
                {/* Plan Details Section */}
                <div className="tw-space-y-6">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-border-b tw-border-gray-200 tw-pb-2">
                    <CreditCard className="tw-w-5 tw-h-5" />
                    Plan Details
                  </div>
                  
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Plan ID</label>
                      <input
                        readOnly={true}
                        type="text"
                        name="planId"
                        value={formData.planId}
                        onChange={handleChange}
                        className="tw-w-full tw-px-4 tw-py-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200"
                      />
                    </div>

                    <div>
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Upcoming Plan</label>
                      <select
                        name="upcomingPlan"
                        value={formData.upcomingPlan}
                        onChange={handleChange}
                        disabled={requesting || formData.cancelOnNextRenew || upcomingPlans == null || upcomingPlans.length == 0}
                        className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200 ${formData.cancelOnNextRenew || upcomingPlans == null || upcomingPlans.length == 0 ? 'tw-bg-gray-100 tw-cursor-not-allowed' : ""}`}
                      >
                        <option value="">Select upcoming plan</option>
                        {upcomingPlans.map((plan) => {
                          return <option key={plan.planId} value={plan.planId}>{plan.plan}</option>
                        })}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Dates Section */}
                <div className="tw-space-y-6">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-border-b tw-border-gray-200 tw-pb-2">
                    <Calendar className="tw-w-5 tw-h-5" />
                    Dates
                  </div>
                  
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                    {renderDateField('endOfGracePeriod', 'End of Grace Period')}
                    {renderDateField('subscriptionStartDate', 'Subscription Start Date')}
                    {renderDateField('nextRenewDate', 'Next Renew Date')}
                  </div>

                </div>
                

                {/* Payment Information Section */}
                <div className="tw-hidden tw-space-y-6">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-border-b tw-border-gray-200 tw-pb-2">
                    <CreditCard className="tw-w-5 tw-h-5" />
                    Payment Information
                  </div>
                  
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Primary Payment Method</label>
                    <input
                      readOnly={true}
                      type="text"
                      name="primaryMethod"
                      value={formData.primaryMethod}
                      onChange={handleChange}
                      className="tw-w-full tw-px-4 tw-py-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200"
                      placeholder="Enter payment method"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                    <div className="tw-flex tw-items-center tw-space-x-3 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
                      <input
                        readOnly={true}
                        disabled={true}
                        type="checkbox"
                        id="updatePaymentMethodRequired"
                        name="updatePaymentMethodRequired"
                        checked={formData.updatePaymentMethodRequired}
                        onChange={handleChange}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 focus:tw-ring-2"
                      />
                      <label htmlFor="updatePaymentMethodRequired" className="tw-text-sm tw-font-medium tw-text-gray-700">
                        Update Payment Method Required
                      </label>
                    </div>

                    <div className="tw-hidden tw-items-center tw-space-x-3 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
                      <input
                        readOnly={true}
                        disabled={true}
                        type="checkbox"
                        id="isPrimaryCardExpired"
                        name="isPrimaryCardExpired"
                        checked={formData.isPrimaryCardExpired}
                        onChange={handleChange}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 focus:tw-ring-2"
                      />
                      <label htmlFor="isPrimaryCardExpired" className="tw-text-sm tw-font-medium tw-text-gray-700">
                        Primary Card Expired
                      </label>
                    </div>

                    <div className="tw-flex tw-items-center tw-space-x-3 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
                      <input
                        readOnly={true}
                        disabled={true}
                        type="checkbox"
                        id="processing"
                        name="processing"
                        checked={formData.processing}
                        onChange={handleChange}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 focus:tw-ring-2"
                      />
                      <label htmlFor="processing" className="tw-text-sm tw-font-medium tw-text-gray-700">
                        Currently Processing
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Main Action Buttons */}
          <div className="tw-flex tw-gap-4 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={requesting}
              className="btn btn-primary"
            >
              Save Changes
            </button>
          </div>
          <div className="tw-flex tw-gap-4 tw-mt-2">
            <button
              type="button"
              onClick={() => { 
                if(onPropertiesUpdate) onPropertiesUpdate(formData.userId)
              }}
              disabled={requesting}
              className="btn btn-primary "
            >
              Update Properties Status Based On Subscription
            </button>
          </div>
        </div>
      </div>
      {requesting && (
        <div className="tw-fixed tw-inset-0 tw-w-full tw-h-full tw-bg-black/40 tw-z-[1000] tw-flex tw-items-center tw-justify-center">
            <div className="loader"></div>
        </div>
      )}
    </div>
  );
}
